import { CrmContactFilter } from '../contactList';
import { MAX_TICKED } from '../../tagging/tagging';

/**
 * Who a bulk change on the contact list applies to (card 19):
 *
 * - **`ids`:** rows ticked one by one, on any page of the same filter. Sent as
 *   `filter: { ids }`, so the API acts on exactly them. At most MAX_TICKED.
 * - **`all`:** everyone the current filter matches, however many pages: the
 *   plan's "select all matching the filter". Sent as the list's own filter.
 *
 * A selection belongs to one filter: changing the search or a filter clears it
 * (CrmContacts keys it by the filter), while paging and sorting keep it.
 */
export type Selection = { kind: 'none' } | { kind: 'ids'; ids: ReadonlyArray<number> } | { kind: 'all' };

export const NO_SELECTION: Selection = { kind: 'none' };

/** What a tick did: the new selection, and whether the ticking limit stopped it. */
export interface SelectionChange {
  selection: Selection;
  capped: boolean;
}

const ofIds = (ids: ReadonlyArray<number>): Selection => (ids.length ? { kind: 'ids', ids } : NO_SELECTION);

export const isSelected = (selection: Selection, id: number) =>
  selection.kind === 'all' || (selection.kind === 'ids' && selection.ids.includes(id));

/** The header checkbox: none, some or all of this page's rows are ticked. */
export const pageState = (selection: Selection, pageIds: ReadonlyArray<number>): 'none' | 'some' | 'all' => {
  if (!pageIds.length || selection.kind === 'none') return 'none';
  if (selection.kind === 'all') return 'all';
  const ticked = pageIds.filter((id) => selection.ids.includes(id)).length;
  return ticked === 0 ? 'none' : ticked === pageIds.length ? 'all' : 'some';
};

/**
 * One row's box. Unticking a row while everyone matching is selected leaves
 * this page's other rows ticked, as a mail client does: "everyone but one" is
 * not a selection the API can be sent.
 */
export const toggleRow = (selection: Selection, id: number, pageIds: ReadonlyArray<number>): SelectionChange => {
  if (selection.kind === 'all') return { selection: ofIds(pageIds.filter((other) => other !== id)), capped: false };
  const ids = selection.kind === 'ids' ? selection.ids : [];
  if (ids.includes(id)) return { selection: ofIds(ids.filter((other) => other !== id)), capped: false };
  if (ids.length >= MAX_TICKED) return { selection, capped: true };
  return { selection: ofIds([...ids, id]), capped: false };
};

/** The header box: ticks every row of this page, or unticks them when all are ticked. */
export const togglePage = (selection: Selection, pageIds: ReadonlyArray<number>): SelectionChange => {
  if (pageState(selection, pageIds) === 'all') {
    if (selection.kind === 'all') return { selection: NO_SELECTION, capped: false };
    const ids = selection.kind === 'ids' ? selection.ids : [];
    return { selection: ofIds(ids.filter((id) => !pageIds.includes(id))), capped: false };
  }
  const ids = selection.kind === 'ids' ? selection.ids : [];
  const added = pageIds.filter((id) => !ids.includes(id));
  if (ids.length + added.length > MAX_TICKED) return { selection, capped: true };
  return { selection: ofIds([...ids, ...added]), capped: false };
};

/** How many are selected: the ticked rows, or the filter's total when everyone is. */
export const selectedCount = (selection: Selection, total: number | undefined): number | undefined =>
  selection.kind === 'ids' ? selection.ids.length : selection.kind === 'all' ? total : 0;

/**
 * What a bulk change is sent: the ticked ids, or the filter as the list sends
 * it. An empty filter is everyone, which is why the change also carries the
 * count the admin confirmed.
 */
export const selectionFilter = (selection: Selection, filter: CrmContactFilter | undefined): CrmContactFilter =>
  selection.kind === 'ids' ? { ids: [...selection.ids] } : (filter ?? {});
